import { DateTime } from 'luxon';

/**
 * Datas de `<input type="date">` vêm como `yyyy-mm-dd`. `new Date('yyyy-mm-dd')` é UTC meia-noite
 * e quebra `getDate()` em fusos atrás de UTC. Use estes helpers para calendário local.
 */
export function parseInputDateYmdLocal(ymd: string, timezone?: string): Date | null {
    const t = ymd?.trim();
    if (!t) {
        return null;
    }
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
    if (!m) {
        return null;
    }
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    if (!y || mo < 1 || mo > 12 || d < 1 || d > 31) {
        return null;
    }
    if (timezone) {
        return DateTime.fromObject({ year: y, month: mo, day: d }, { zone: timezone }).toJSDate();
    }
    return new Date(y, mo - 1, d);
}

export function formatDateDdMmYyyyLocal(d: Date, timezone?: string): string {
    if (timezone) {
        return DateTime.fromJSDate(d, { zone: timezone }).toFormat('dd/MM/yyyy');
    }
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
}

export function formatDateYmdLocal(d: Date, timezone?: string): string {
    if (timezone) {
        return DateTime.fromJSDate(d, { zone: timezone }).toFormat('yyyy-MM-dd');
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
}

/** Primeiro instante do dia local. */
export function startOfLocalDay(d: Date, timezone?: string): Date {
    if (timezone) {
        return DateTime.fromJSDate(d, { zone: timezone }).startOf('day').toJSDate();
    }
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

/** Último instante do dia local (inclusivo em ranges `<=`). */
export function endOfLocalDay(d: Date, timezone?: string): Date {
    if (timezone) {
        return DateTime.fromJSDate(d, { zone: timezone }).endOf('day').toJSDate();
    }
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

export function startOfMonth(d: Date, timezone?: string): Date {
    if (timezone) {
        return DateTime.fromJSDate(d, { zone: timezone }).startOf('month').toJSDate();
    }
    return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
}

/** Fim do mês de `d` em local, ou “agora” se o mês for o atual e `capAtNow` for true. */
export function endOfMonthOrNow(d: Date, capAtNow = true, timezone?: string): Date {
    if (timezone) {
        const now = DateTime.now().setZone(timezone);
        const dt = DateTime.fromJSDate(d, { zone: timezone });
        const sameMonth = dt.year === now.year && dt.month === now.month;
        if (capAtNow && sameMonth) {
            return now.toJSDate();
        }
        return dt.endOf('month').toJSDate();
    }
    const now = new Date();
    const sameMonth =
        d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    if (capAtNow && sameMonth) {
        return now;
    }
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
    return last;
}

/** Início do mês corrente (local). */
export function startOfCurrentMonth(timezone?: string): Date {
    return startOfMonth(new Date(), timezone);
}

/** `from` = primeiro dia do mês de `ref`; `to` = endOfMonthOrNow(ref). */
export function rangeForCalendarMonth(ref: Date, capAtNow = true, timezone?: string): { from: Date; to: Date } {
    return {
        from: startOfMonth(ref, timezone),
        to: endOfMonthOrNow(ref, capAtNow, timezone),
    };
}

/** Janela rolante: de hoje − N meses (mesmo dia do mês, ajustado pelo JS) até agora. */
export function rangeLastNMonths(n: number, timezone?: string): { from: Date; to: Date } {
    if (timezone) {
        const to = DateTime.now().setZone(timezone);
        const from = to.minus({ months: n });
        return {
            from: startOfLocalDay(from.toJSDate(), timezone),
            to: to.toJSDate(),
        };
    }
    const to = new Date();
    const from = new Date(to);
    from.setMonth(from.getMonth() - n);
    return { from: startOfLocalDay(from), to };
}

/** Mês civil imediatamente anterior, completo (ex.: em abril → 1/03–último dia de março). */
export function rangePreviousCalendarMonth(ref: Date = new Date(), timezone?: string): { from: Date; to: Date } {
    if (timezone) {
        const dt = DateTime.fromJSDate(ref, { zone: timezone }).minus({ months: 1 });
        return {
            from: dt.startOf('month').toJSDate(),
            to: dt.endOf('month').toJSDate(),
        };
    }
    const y = ref.getFullYear();
    const m = ref.getMonth();
    const from = new Date(y, m - 1, 1, 0, 0, 0, 0);
    const lastDayPrev = new Date(y, m, 0);
    return { from, to: endOfLocalDay(lastDayPrev) };
}

/**
 * N meses civis incluindo o mês de `ref`: do dia 1 do primeiro mês até agora (ou fim do mês de `ref`).
 * Ex.: n=3 em abril → fevereiro, março e abril até o instante `ref` (ou último dia de abril se capAtNow false).
 */
export function rangeLastNCalendarMonthsThroughNow(
    n: number,
    ref: Date = new Date(),
    capAtNow = true,
    timezone?: string,
): { from: Date; to: Date } {
    const count = Math.max(1, Math.floor(n));
    if (timezone) {
        const dt = DateTime.fromJSDate(ref, { zone: timezone });
        const from = dt.minus({ months: count - 1 }).startOf('month').toJSDate();
        const to = capAtNow ? dt.toJSDate() : dt.endOf('month').toJSDate();
        return { from, to };
    }
    const y = ref.getFullYear();
    const m = ref.getMonth();
    const from = new Date(y, m - (count - 1), 1, 0, 0, 0, 0);
    const to = capAtNow ? new Date(ref.getTime()) : endOfLocalDay(new Date(y, m + 1, 0));
    return { from, to };
}
